'use client';

import React, { useState } from 'react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

interface BookedRange {
  property: string;
  color: string;
  start: number;
  end: number;
}

const bookedRanges: BookedRange[] = [
  { property: 'Lakeside Villa', color: 'bg-rose-400', start: 25, end: 30 },
  { property: 'Kampala Apt', color: 'bg-blue-400', start: 1, end: 4 },
  { property: 'Bwindi Lodge', color: 'bg-emerald-400', start: 10, end: 14 },
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function getDayStatus(day: number, ranges: BookedRange[]) {
  for (const r of ranges) {
    if (day >= r.start && day <= r.end) return r;
  }
  return null;
}

export default function HostCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedProperty, setSelectedProperty] = useState<string>('all');

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const filteredRanges = selectedProperty === 'all' ? bookedRanges : bookedRanges.filter(r => r.property === selectedProperty);

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Booked Days', value: '18', sub: 'This month' },
          { label: 'Available Days', value: '13', sub: 'This month' },
          { label: 'Blocked Days', value: '0', sub: 'Maintenance' },
          { label: 'Occupancy Rate', value: '58%', sub: 'August 2026' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-sm font-semibold text-gray-700 mt-0.5">{s.label}</div>
            <div className="text-xs text-gray-400 mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="font-bold text-gray-900">{MONTHS[month]} {year}</h3>
            <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const booked = getDayStatus(day, filteredRanges);
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <div
                  key={day}
                  className={`relative aspect-square flex items-center justify-center rounded-xl text-sm font-semibold cursor-pointer transition-all
                    ${booked ? `${booked.color} text-white` : 'hover:bg-gray-100 text-gray-700'}
                    ${isToday && !booked ? 'ring-2 ring-rose-400 text-rose-500' : ''}
                  `}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
            {bookedRanges.map((r) => (
              <div key={r.property} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded-full ${r.color}`} />
                <span className="text-xs text-gray-600">{r.property}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full ring-2 ring-rose-400" />
              <span className="text-xs text-gray-600">Today</span>
            </div>
          </div>
        </div>

        {/* Upcoming bookings sidebar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">Upcoming Stays</h3>
          <div className="mb-4">
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-rose-300"
            >
              <option value="all">All Properties</option>
              {bookedRanges.map((r) => (
                <option key={r.property} value={r.property}>{r.property}</option>
              ))}
            </select>
          </div>
          <div className="space-y-3">
            {[
              { guest: 'Sarah N.', property: 'Lakeside Villa', dates: 'Aug 25–30', nights: 5, color: 'border-rose-400' },
              { guest: 'James O.', property: 'Kampala Apt', dates: 'Sep 1–4', nights: 3, color: 'border-blue-400' },
              { guest: 'Emma L.', property: 'Bwindi Lodge', dates: 'Sep 10–14', nights: 4, color: 'border-emerald-400' },
            ].map((b, i) => (
              <div key={i} className={`border-l-4 ${b.color} pl-3 py-1`}>
                <div className="text-sm font-semibold text-gray-800">{b.guest}</div>
                <div className="text-xs text-gray-500">{b.property}</div>
                <div className="text-xs text-gray-400 mt-0.5">{b.dates} · {b.nights} nights</div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <button className="w-full bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold text-sm py-2.5 rounded-xl transition-colors">
              + Block Dates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
